// The project's Sourcing tab, first: who makes each build (Mitch, 2026-11-04: "The formatting on the sourcing tab of a
// project is messy under the 'Who makes each build'"): one row per build (each product and sub-assembly the project
// builds) with its maker (a pick changes it: bom.set_maker, on the revision in use and any draft, for make orders from
// now on), its revision, how many parts go into it and a link to its board. Who supplies each part is the next table
// (Parts and who supplies them, PartsTabs.tsx), not here.
import { ArrowRight, Cpu } from "lucide-react";
import { revisionState, variantsOf } from "../../../../shared/boms.ts";
import type { ScopeFamily } from "../../../../shared/projects.ts";
import { bomLink, bomPlaceFor, STATE_TONE } from "../../lib/boms.ts";
import { skusText } from "../../lib/format.ts";
import { can, mutate } from "../../lib/mutate.ts";
import { Link } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Badge, Select } from "../../ui/controls.tsx";
import { CellControls, type Column, DataTable } from "../../ui/DataTable.tsx";
import { PageSection } from "../../ui/PageParts.tsx";
import { usePaneOpen } from "../../ui/SidePane.tsx";
import { useBoardProject } from "../BoardTab.tsx";
import type { TabProps } from "./Workspace.tsx";

type BuildRow = { id: string; f: ScopeFamily; sku: string; order: number; parts: number };

export function BuildSupply({ px, scope }: TabProps) {
  const ix = px.ix;
  const vendors = useRows("vendor");
  const openPane = usePaneOpen();
  if (!scope.families.length) return null;
  const makers = vendors.filter((v) => !v.archived && v.kind !== "supplier").sort((a, b) => (a.short_name || a.name).localeCompare(b.short_name || b.name));
  // Products first, then the sub-assemblies, in the order the project reaches them
  const rows: BuildRow[] = [...scope.families]
    .sort((a, b) => Number(a.kind === "subassembly") - Number(b.kind === "subassembly"))
    .map((f, order) => ({
      id: f.key,
      f,
      sku: skusText(variantsOf(ix, f.bom).map((v) => v.sku)),
      order,
      parts: (ix.linesByBom.get(f.bom.id) ?? []).length,
    }));
  const columns: Column<BuildRow>[] = [
    // SKU, then Name, as every table of items starts
    { key: "sku", header: "SKU", width: "minmax(110px,150px)", cell: (r) => <span className="truncate font-mono text-[12px]">{r.sku}</span> },
    {
      key: "label",
      header: "Name",
      width: "minmax(200px,2fr)",
      value: (r) => r.f.label,
      cell: (r) => <span className="truncate font-medium">{r.f.label}</span>,
    },
    { key: "kind", header: "Kind", width: "110px", value: (r) => (r.f.kind === "product" ? "Product" : "Sub-assembly") },
    {
      key: "maker",
      header: "Made by",
      headerTitle: "Who builds it: its make orders go to them. A sub-assembly with nobody is built by whoever builds what uses it",
      width: "210px",
      value: (r) => {
        const v = r.f.bom.make_vendor_id ? px.vendorById.get(r.f.bom.make_vendor_id) : undefined;
        return v ? v.short_name || v.name : r.f.kind === "subassembly" ? "Whoever builds what uses it" : "Not chosen";
      },
      cell: (r) => (
        <CellControls className="w-full">
          <Select
            aria-label={`Who makes ${r.f.label}`}
            value={r.f.bom.make_vendor_id ?? ""}
            disabled={!can("bom.set_maker")}
            onChange={(e) => mutate("bom.set_maker", { bom_id: r.f.bom.id, make_vendor_id: e.target.value || null })}
            options={[
              { value: "", label: r.f.kind === "subassembly" ? "Whoever builds what uses it" : "Not chosen" },
              ...makers.map((v) => ({ value: v.id, label: v.short_name || v.name })),
            ]}
            className="h-7 text-[12px]"
          />
        </CellControls>
      ),
    },
    {
      key: "revision",
      header: "BOM",
      width: "150px",
      value: (r) => r.f.bom.revision,
      cell: (r) => <Revision f={r.f} ix={ix} />,
    },
    {
      key: "parts",
      header: "Parts",
      headerTitle: "Lines on its BOM",
      width: "70px",
      align: "right",
      cell: (r) => <span className="font-mono text-[12px]">{r.parts}</span>,
    },
    { key: "board", header: "", width: "180px", value: () => "", cell: (r) => <BoardLink f={r.f} /> },
  ];
  return (
    <PageSection title="Who makes each build" note="A change goes to make orders from now on; ones already placed keep theirs">
      {(slot) => (
        <DataTable
          fit={20}
          rows={rows}
          columns={columns}
          defaultSort={{ key: "order" }}
          onOpen={(r) => r.f.bom.item_id && openPane("item", r.f.bom.item_id)}
          exportName="Who makes each build"
          bar={slot}
          pageFilters={false}
          rowNoun={["build", "builds"]}
        />
      )}
    </PageSection>
  );
}

/** Its revision in use (and a draft beside it, when there's one), each opening its BOM. */
function Revision({ f, ix }: { f: ScopeFamily; ix: TabProps["px"]["ix"] }) {
  const working = f.bom;
  const state = revisionState(ix, working);
  const draft = (ix.families.get(f.key) ?? []).find((b) => b.status === "draft");
  return (
    <CellControls className="gap-2 text-[12px]">
      <Link to={bomLink(working.id)} className="flex items-center gap-1 hover:underline">
        Rev {working.revision} <Badge tone={STATE_TONE[state]}>{state}</Badge>
      </Link>
      {draft && draft.id !== working.id && (
        <Link to={bomLink(draft.id)} className="text-muted hover:underline" title="A change to its parts, not released yet">
          Rev {draft.revision} draft
        </Link>
      )}
    </CellControls>
  );
}

/** A board's components (from its KiCad project), for a sub-assembly that has one. */
function BoardLink({ f }: { f: ScopeFamily }) {
  const board = useBoardProject(f.kind === "subassembly" ? f.bom.item_id : null);
  if (!board) return null;
  const place = bomPlaceFor(location.pathname);
  return (
    <CellControls>
      <Link
        to={`${bomLink(f.bom.id)}&${place.tab}=board`}
        className="flex items-center gap-1 text-[12px] text-accent-text hover:underline"
        title="The board's components and who supplies each (from its KiCad project)"
      >
        <Cpu className="size-3.5" /> Its board <ArrowRight className="size-3" />
      </Link>
    </CellControls>
  );
}
